// src/components/leitner/QuestionCard.jsx
// Updated to handle all response types: multiselect, short_answer, paragraph, matching, ordering

import React, { useState, useMemo, useEffect } from 'react';
import {
  Lightbulb, ChevronRight, GripVertical, Check, X,
  Calculator, Link2, Plus, Trash2, ArrowUpDown
} from 'lucide-react';
import { MathRenderer } from '../math/MathRenderer';
import QuestionMediaDisplay from './QuestionMediaDisplay';
import { RESPONSE_TYPES } from './ResponseTypeInputs';
import InlineMathField from '../math/InlineMathField';
import { SteppedCalculationAnswer } from './SteppedCalculationInput';

// ============== MATCHING COMPONENT ==============
const MatchingAnswer = ({ prompts, matchItems, matchedPairs, setMatchedPairs, isSubmitted }) => {
  const [dragItem, setDragItem] = useState(null);

  const shuffledItems = useMemo(() => {
    return [...matchItems].sort(() => Math.random() - 0.5);
  }, [matchItems]);

  const handleDrop = (promptId, itemId) => {
    setMatchedPairs(prev => {
      const newPairs = { ...prev };
      // Remove item from existing match
      Object.keys(newPairs).forEach(key => {
        if (newPairs[key] === itemId) delete newPairs[key];
      });
      newPairs[promptId] = itemId;
      return newPairs;
    });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">Drag items to match:</p>

      {/* Match zones */}
      <div className="space-y-2">
        {prompts.map(prompt => {
          const matchedItemId = matchedPairs[prompt.id];
          const matchedItem = matchItems.find(i => i.id === matchedItemId);
          const isCorrect = isSubmitted && matchedItemId === prompt.correctMatchId;
          const isWrong = isSubmitted && matchedItemId && matchedItemId !== prompt.correctMatchId;

          return (
            <div
              key={prompt.id}
              className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${isCorrect ? 'border-green-500 bg-green-900/20' :
                isWrong ? 'border-red-500 bg-red-900/20' :
                  'border-slate-600 bg-slate-800'
                }`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (!isSubmitted) handleDrop(prompt.id, e.dataTransfer.getData('text'));
              }}
            >
              <div className="flex-1 text-slate-200">
                <MathRenderer content={prompt.text || prompt.prompt} enableMath={true} />
              </div>
              <Link2 size={16} className="text-slate-500" />
              <div className={`flex-1 min-h-[40px] flex items-center justify-center rounded ${matchedItem ? 'bg-slate-700 px-3 py-2' : 'bg-slate-900/50 border border-dashed border-slate-600'
                }`}>
                {matchedItem ? (
                  <span className="text-slate-200">
                    <MathRenderer content={matchedItem.text || matchedItem.match} enableMath={true} />
                  </span>
                ) : (
                  <span className="text-slate-500 text-sm">Drop here</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Draggable items */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-700">
        {shuffledItems.map(item => {
          const isUsed = Object.values(matchedPairs).includes(item.id);
          return (
            <div
              key={item.id}
              draggable={!isUsed && !isSubmitted}
              onDragStart={(e) => e.dataTransfer.setData('text', item.id)}
              className={`px-4 py-2 rounded-lg border transition-all ${isUsed ? 'border-slate-700 bg-slate-800/50 text-slate-500 cursor-not-allowed' :
                isSubmitted ? 'border-slate-600 bg-slate-700 cursor-not-allowed' :
                  'border-slate-500 bg-slate-700 hover:border-indigo-400 text-slate-200 cursor-grab'
                }`}
            >
              <MathRenderer content={item.text || item.match} enableMath={true} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============== ORDERING COMPONENT ==============
const OrderingAnswer = ({ items, orderedItems, setOrderedItems, isSubmitted, correctOrder }) => {
  const moveItem = (index, direction) => {
    if (isSubmitted) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= orderedItems.length) return;

    const newItems = [...orderedItems];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setOrderedItems(newItems);
  };

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-400 mb-3">Arrange in the correct order:</p>
      {orderedItems.map((item, index) => {
        const correctIndex = correctOrder?.indexOf(item.id);
        const isCorrectPosition = isSubmitted && index === correctIndex;
        const isWrongPosition = isSubmitted && index !== correctIndex;

        return (
          <div
            key={item.id}
            className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${isCorrectPosition ? 'border-green-500 bg-green-900/20' :
              isWrongPosition ? 'border-red-500 bg-red-900/20' :
                'border-slate-600 bg-slate-700'
              }`}
          >
            {!isSubmitted && (
              <div className="flex flex-col gap-1">
                <button onClick={() => moveItem(index, 'up')} disabled={index === 0}
                  className="p-1 text-slate-400 hover:text-white disabled:opacity-30">
                  <ChevronRight size={16} className="rotate-[-90deg]" />
                </button>
                <button onClick={() => moveItem(index, 'down')} disabled={index === orderedItems.length - 1}
                  className="p-1 text-slate-400 hover:text-white disabled:opacity-30">
                  <ChevronRight size={16} className="rotate-90" />
                </button>
              </div>
            )}
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-600 text-slate-300 text-sm font-medium">
              {index + 1}
            </span>
            <span className="flex-1 text-slate-200">
              <MathRenderer content={item.text} enableMath={true} />
            </span>
            {isSubmitted && (
              isCorrectPosition
                ? <Check className="text-green-400" size={20} />
                : <X className="text-red-400" size={20} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============== TEXT INPUT COMPONENT ==============
const TextAnswerInput = ({ value, onChange, disabled, multiline = false }) => {
  if (multiline) {
    return (
      <InlineMathField
        value={value}
        onChange={onChange}
        rows={5}
        placeholder="Type your answer... (Ctrl+M for math)"
        disabled={disabled}
      />
    );
  }

  return (
    <InlineMathField
      value={value}
      onChange={onChange}
      rows={1}
      placeholder="Type your answer... (Ctrl+M for math)"
      disabled={disabled}
    />
  );
};

// ============== CALCULATION STEPS ANSWER (Learner) ==============
const CalculationStepsAnswer = ({ value = [], onChange, finalAnswer, onFinalAnswerChange, disabled }) => {
  const addStep = () => {
    if (value.length >= 15) return;
    onChange([...value, { id: `ans_step_${Date.now()}`, text: '' }]);
  };

  const updateStep = (index, text) => {
    const updated = [...value];
    updated[index] = { ...updated[index], text };
    onChange(updated);
  };

  const removeStep = (index) => {
    if (value.length <= 1) return;
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-300">Show your working</label>
        {!disabled && (
          <button
            type="button"
            onClick={addStep}
            disabled={value.length >= 15}
            className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 disabled:opacity-50"
          >
            <Plus size={14} /> Add Step
          </button>
        )}
      </div>

      <div className="space-y-2">
        {value.map((step, index) => (
          <div key={step.id} className="flex items-center gap-2">
            <span className="w-7 h-7 flex items-center justify-center bg-cyan-500/20 border border-cyan-500/40 rounded-full text-sm text-cyan-300 font-medium">
              {index + 1}
            </span>
            <div className="flex-1">
              <InlineMathField
                value={step.text}
                onChange={(text) => updateStep(index, text)}
                rows={1}
                placeholder={`Step ${index + 1}... (Ctrl+M for math)`}
                disabled={disabled}
              />
            </div>
            {!disabled && value.length > 1 && (
              <button type="button" onClick={() => removeStep(index)} className="p-1 text-red-400 hover:text-red-300">
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="pt-2 border-t border-slate-700">
        <label className="block text-sm font-medium text-slate-300 mb-1">Final Answer</label>
        <InlineMathField
          value={finalAnswer}
          onChange={onFinalAnswerChange}
          rows={1}
          placeholder="Your final answer (Ctrl+M for math)"
          disabled={disabled}
        />
      </div>
    </div>
  );
};

// ============== MAIN QUESTION CARD ==============
const QuestionCard = ({
  question,
  onSubmit,
  stemText,
  stemImage,
  stemMedia,
  progress = null,
  sessionAttempts = 0,
  maxAttempts = 3,
  onRequestHint,
  isRetrying = false,
  parentQuestion // Add parentQuestion context
}) => {
  const responseType = question.responseType || RESPONSE_TYPES.MULTISELECT;

  // State for different response types
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [textAnswer, setTextAnswer] = useState('');
  const [matchedPairs, setMatchedPairs] = useState({});
  const [orderedItems, setOrderedItems] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [calculationSteps, setCalculationSteps] = useState([{ id: `step_${Date.now()}`, text: '' }]);
  const [stepsAnswer, setStepsAnswer] = useState([{ id: 'ans_step_1', text: '' }]);

  // Shuffle options for multiselect
  const shuffledOptions = useMemo(() => {
    if (responseType !== RESPONSE_TYPES.MULTISELECT || !question.options || !Array.isArray(question.options)) return [];
    return [...question.options].sort(() => Math.random() - 0.5);
  }, [question.id, responseType]);

  // Initialize ordering items
  useEffect(() => {
    if (responseType === RESPONSE_TYPES.ORDERING && question.orderingItems) {
      setOrderedItems([...question.orderingItems].sort(() => Math.random() - 0.5));
    }
  }, [question.id, responseType]);

  // Reset on retry
  useEffect(() => {
    if (isRetrying) {
      setSelectedOptionIds([]);
      setTextAnswer('');
      setMatchedPairs({});
      if (responseType === RESPONSE_TYPES.ORDERING && question.orderingItems) {
        setOrderedItems([...question.orderingItems].sort(() => Math.random() - 0.5));
      }
      setIsSubmitted(false);
      setIsCorrect(false);
    }
  }, [isRetrying, question.id]);

  const handleOptionToggle = (optionId) => {
    setSelectedOptionIds(prev =>
      prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]
    );
  };

  const handleSubmit = () => {
    let correct = false;

    switch (responseType) {
      case RESPONSE_TYPES.MULTISELECT:
        correct = selectedOptionIds.length === question.correctOptionIds.length &&
          selectedOptionIds.every(id => question.correctOptionIds.includes(id));
        break;

      case RESPONSE_TYPES.SHORT_ANSWER:
        const normalizedAnswer = textAnswer.trim().toLowerCase();
        const acceptables = [question.correctAnswer, ...(question.acceptableAnswers || [])];
        correct = acceptables.some(ans =>
          question.caseSensitive
            ? textAnswer.trim() === ans.trim()
            : normalizedAnswer === ans.toLowerCase().trim()
        );
        break;

      case RESPONSE_TYPES.CALCULATION:
        correct = null; // AI-assisted, manual review
        break;

      case RESPONSE_TYPES.PARAGRAPH:
        correct = null; // Manual grading
        break;

      case RESPONSE_TYPES.MATCHING:
        const prompts = question.matchingPairs || question.prompts || [];
        correct = prompts.every(p => {
          const correctMatchId = p.correctMatchId || prompts.find(pr => pr.id === p.id)?.correctMatchId;
          return matchedPairs[p.id] === correctMatchId;
        });
        break;

      case RESPONSE_TYPES.ORDERING:
        const correctOrder = question.correctOrder || question.orderingItems?.map(i => i.id);
        correct = orderedItems.every((item, idx) => item.id === correctOrder[idx]);
        break;
    }

    setIsCorrect(correct);
    setIsSubmitted(true);
  };

  const handleNext = () => {
    onSubmit(isCorrect, {
      responseType,
      answer: responseType === RESPONSE_TYPES.MULTISELECT ? selectedOptionIds :
        responseType === RESPONSE_TYPES.MATCHING ? matchedPairs :
          responseType === RESPONSE_TYPES.ORDERING ? orderedItems.map(i => i.id) :
            responseType === RESPONSE_TYPES.CALCULATION
              ? calculationSteps.filter(s => s.text?.trim()) :
              textAnswer
    });
    setSelectedOptionIds([]);
    setTextAnswer('');
    setMatchedPairs({});
    setIsSubmitted(false);
    setIsCorrect(false);
  };

  const canRetry = !isCorrect && isSubmitted && sessionAttempts < maxAttempts - 1;
  const hasHint = question.hint?.text || question.hint?.audio || question.hintAudio;

  const getOptionClasses = (optionId) => {
    if (!isSubmitted) {
      return selectedOptionIds.includes(optionId)
        ? 'border-indigo-500 bg-indigo-900/30'
        : 'border-slate-600 bg-slate-700 hover:border-slate-500';
    }
    const isCorrectOption = question.correctOptionIds.includes(optionId);
    const wasSelected = selectedOptionIds.includes(optionId);
    if (isCorrect && isCorrectOption) return 'border-green-500 bg-green-900/30';
    if (wasSelected && !isCorrectOption) return 'border-red-500 bg-red-900/30';
    return 'border-slate-600 bg-slate-700';
  };

  const canSubmit = useMemo(() => {
    switch (responseType) {
      case RESPONSE_TYPES.MULTISELECT: return selectedOptionIds.length > 0;
      case RESPONSE_TYPES.SHORT_ANSWER:
      case RESPONSE_TYPES.CALCULATION:
        return calculationSteps.some(s => s.text?.trim());
      case RESPONSE_TYPES.PARAGRAPH: return textAnswer.trim().length > 0;
      case RESPONSE_TYPES.MATCHING:
        const prompts = question.matchingPairs || question.prompts || [];
        return prompts.every(p => matchedPairs[p.id]);
      case RESPONSE_TYPES.ORDERING: return orderedItems.length > 0;
      default: return false;
    }
  }, [responseType, selectedOptionIds, textAnswer, matchedPairs, orderedItems, question]);

  return (
    <div className="bg-slate-800 p-4 sm:p-6 rounded-xl shadow-xl overflow-hidden">
      {/* Parent Context Header */}
      {parentQuestion && (
        <div className="-mt-4 -mx-4 sm:-mt-6 sm:-mx-6 mb-6 bg-indigo-900/30 border-b border-indigo-500/30 p-4 animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-2 text-indigo-400 mb-2">
            <ArrowUpDown size={14} className="rotate-90" />
            <span className="text-[10px] uppercase font-bold tracking-wider">Main Question Context</span>
          </div>
          <div className="text-slate-300 text-sm italic">
            <MathRenderer content={parentQuestion.text || parentQuestion.stemText || ''} enableMarkdown={true} enableMath={true} />
          </div>
        </div>
      )}
      {/* Attempt indicator */}
      {sessionAttempts > 0 && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="text-slate-400">Attempt {sessionAttempts + 1} of {maxAttempts}</span>
          <div className="flex gap-1">
            {[...Array(maxAttempts)].map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full ${i < sessionAttempts ? 'bg-red-500' : i === sessionAttempts ? 'bg-amber-500' : 'bg-slate-600'
                }`} />
            ))}
          </div>
        </div>
      )}

      {/* Stem content */}
      {stemText && (
        <div className="mb-6 p-4 bg-slate-700/50 rounded-lg border border-slate-600">
          <h4 className="text-sm font-bold text-indigo-400 mb-2">CONTEXT:</h4>
          <div className="prose prose-invert max-w-none text-[15px]">
            <MathRenderer content={stemText} enableMarkdown={true} enableMath={true} />
          </div>
          {stemMedia && stemMedia.type !== 'none' && (
            <div className="mt-3">
              <QuestionMediaDisplay media={stemMedia} />
            </div>
          )}
        </div>
      )}

      {/* Question text */}
      <div className="mb-4">
        <div className="prose prose-invert max-w-none text-[15px]">
          <MathRenderer content={question.text} enableMarkdown={true} enableMath={true} />
        </div>
      </div>

      {/* Question media */}
      {!stemText && question.media && question.media.type !== 'none' && (
        <div className="mb-4">
          <QuestionMediaDisplay media={question.media} />
        </div>
      )}

      {progress && (
        <p className="text-center text-sm text-slate-400 mb-6">
          Sub-question {progress.current} of {progress.total}
        </p>
      )}

      {/* RESPONSE AREA */}
      <div className="my-6">
        {/* Multiselect */}
        {responseType === RESPONSE_TYPES.MULTISELECT && (
          <div className="space-y-3">
            {shuffledOptions.map(option => (
              <label key={option.id} className={`flex items-center p-4 rounded-lg border-2 transition-all cursor-pointer ${getOptionClasses(option.id)}`}>
                <input
                  type="checkbox"
                  checked={selectedOptionIds.includes(option.id)}
                  onChange={() => handleOptionToggle(option.id)}
                  disabled={isSubmitted}
                  className="h-5 w-5 rounded bg-slate-600 border-slate-500 text-indigo-500 mr-4 disabled:opacity-50"
                />
                <span className="text-slate-200 flex-grow">
                  <MathRenderer content={option.text} enableMath={true} />
                </span>
              </label>
            ))}
          </div>
        )}

        {/* Short Answer */}
        {responseType === RESPONSE_TYPES.SHORT_ANSWER && (
          <div>
            <TextAnswerInput value={textAnswer} onChange={setTextAnswer} disabled={isSubmitted} />
            {isSubmitted && !isCorrect && question.correctAnswer && (
              <div className="mt-3 p-3 bg-green-900/20 border border-green-600 rounded-lg">
                <p className="text-sm text-green-400">
                  <span className="font-medium">Correct: </span>
                  <MathRenderer content={question.correctAnswer} enableMath={true} />
                </p>
              </div>
            )}
          </div>
        )}

        {/* Calculation Steps */}
        {responseType === RESPONSE_TYPES.CALCULATION_STEPS && (
          <div>
            <CalculationStepsAnswer
              value={stepsAnswer}
              onChange={setStepsAnswer}
              finalAnswer={textAnswer}
              onFinalAnswerChange={setTextAnswer}
              disabled={isSubmitted}
            />
            {isSubmitted && (
              <div className="mt-3 p-3 bg-blue-900/20 border border-blue-600 rounded-lg">
                <p className="text-sm text-blue-400">Your working has been submitted for review.</p>
              </div>
            )}
          </div>
        )}

        {/* Paragraph */}
        {responseType === RESPONSE_TYPES.PARAGRAPH && (
          <div>
            <TextAnswerInput value={textAnswer} onChange={setTextAnswer} disabled={isSubmitted} multiline />
            {isSubmitted && (
              <div className="mt-3 p-3 bg-blue-900/20 border border-blue-600 rounded-lg">
                <p className="text-sm text-blue-400">Your answer has been submitted for review.</p>
              </div>
            )}
          </div>
        )}

        {/* Matching */}
        {responseType === RESPONSE_TYPES.MATCHING && (
          <MatchingAnswer
            prompts={question.matchingPairs || question.prompts || []}
            matchItems={question.matchingPairs?.map(p => ({ id: p.id + '_match', text: p.match, ...p })) || question.matchItems || []}
            matchedPairs={matchedPairs}
            setMatchedPairs={setMatchedPairs}
            isSubmitted={isSubmitted}
          />
        )}

        {/* Ordering */}
        {responseType === RESPONSE_TYPES.ORDERING && orderedItems.length > 0 && (
          <OrderingAnswer
            items={question.orderingItems}
            orderedItems={orderedItems}
            setOrderedItems={setOrderedItems}
            isSubmitted={isSubmitted}
            correctOrder={question.correctOrder}
          />
        )}
      </div>

      {/* Submit / Next */}
      {!isSubmitted ? (
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-lg transition disabled:bg-slate-600 disabled:cursor-not-allowed"
        >
          Submit Answer
        </button>
      ) : (
        <div className="space-y-3">
          {isCorrect !== null && (
            <p className={`text-xl font-bold text-center ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
              {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
            </p>
          )}

          {responseType === RESPONSE_TYPES.PARAGRAPH && (
            <p className="text-center text-blue-400">Submitted for review</p>
          )}

          {canRetry && hasHint && (
            <button
              onClick={() => onRequestHint?.(question)}
              className="w-full py-3 px-4 rounded-lg border-2 border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 font-medium flex items-center justify-center gap-2"
            >
              <Lightbulb className="h-5 w-5" />
              View Hint & Try Again
              <span className="text-xs opacity-70">({maxAttempts - sessionAttempts - 1} left)</span>
            </button>
          )}

          <button
            onClick={handleNext}
            className="w-full bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2"
          >
            {isCorrect ? 'Next' : (canRetry ? 'Skip to Next' : 'Next')}
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;